import {useMutation} from '@tanstack/react-query';
import {UseFormReturn} from 'react-hook-form';
import {BackendResponse} from '../../http/backend-response/backend-response';
import {onFormQueryError} from '../../errors/on-form-query-error';
import {apiClient} from '../../http/query-client';
import {useAuth} from '../use-auth';

interface Response extends BackendResponse {
  bootstrapData?: string;
  message?: string;
  status: 'success' | 'needs_email_verification';
  redirectUrl?: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  password_confirmation: string;
}

export function useRegister(form: UseFormReturn<RegisterPayload>) {
  const {getRedirectUri} = useAuth();

  return useMutation({
    mutationFn: register,
    onSuccess: response => {
      // Full page reload to ensure fresh bootstrap data and clear all cached state
      if (response.status === 'needs_email_verification') {
        window.location.href = '/';
        return;
      }

      // If user arrived via OAuth (e.g. MCP connector), redirect back to authorize
      // Only after email is verified (status is 'success')
      if (response.redirectUrl) {
        window.location.href = response.redirectUrl;
        return;
      }

      // Parse bootstrapData if it's a string
      let user;
      if (typeof response.bootstrapData === 'string') {
        try {
          const parsed = JSON.parse(response.bootstrapData);
          user = parsed.user;
        } catch {
          user = undefined;
        }
      } else if (typeof response.bootstrapData === 'object' && response.bootstrapData !== null) {
        user = (response.bootstrapData as { user?: import('../user').User }).user;
      }
      // If user has an active Autumn subscription, go to dashboard; otherwise pricing
      if (user?.autumn_subscription_active) {
        window.location.href = getRedirectUri();
      } else {
        window.location.href = '/pricing';
      }
    },
    onError: r => onFormQueryError(r, form),
  });
}

function register(payload: RegisterPayload): Promise<Response> {
  return apiClient
    .post('auth/register', payload)
    .then(response => response.data);
}
